import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Slide,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FormStepper from 'src/components/FormStepper/FormStepper';
import {
  CLEAR_CONTRACT_TRANSACTIONS,
  loadContractTransaction
} from 'src/store/actions/transactionActions';
import { selectTransactionBalance } from 'src/store/selectors/transactionSelectors';
import { formatAmountWithDecimals } from 'src/utils/formatAmout';
import { loadEquipmentDetail } from 'src/store/actions/equipmentsActions';
import { selectEquipmentDetail } from 'src/store/selectors/equipmentSelectors';
import AlertWithLink from 'src/components/Alert/AlertWithLink/AlertWithLink';
import { Link } from 'react-router-dom';
import CurrentStep from './CurrentStep';
import useStyles from './styles';
import makeInitialState from './initialState';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const stepperLabels = [
  'CONTRACT_VIEW.CONTRACT_DETAIL.CREATE_CONTRIBUTION',
  'COMMON.CONFIRMATION'
];

const ContributionsForm = ({
  open,
  handleClose,
  handleCreateContribution,
  currentRent,
  contractId,
  equipmentId
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const initialState = useMemo(() => makeInitialState(currentRent), [currentRent]);
  const [step, setStep] = useState(0);
  const [data, setData] = useState(initialState);
  const balance = useSelector(selectTransactionBalance);
  const equipment = useSelector(selectEquipmentDetail);

  const resetRef = useRef();

  useEffect(() => {
    dispatch(loadContractTransaction(contractId, { page: 0 }));
    return () => {
      dispatch({ type: CLEAR_CONTRACT_TRANSACTIONS });
    };
  }, [dispatch, contractId]);

  useEffect(() => {
    return () => {
      setStep(0);
    };
  }, [open]);

  const handleNext = useCallback(
    values => {
      setData({ ...data, ...values });
      setStep(step + 1);
    },
    [setStep, step, setData, data]
  );

  const handleCloseDialog = useCallback(() => {
    if (resetRef.current) {
      resetRef.current();
    }
    setData(initialState);
    setStep(0);
    handleClose();
  }, [handleClose, initialState]);

  const handleBackOrClose = useCallback(() => {
    if (step === 0) {
      if (resetRef.current) {
        resetRef.current();
      }
      setData(initialState);
      setStep(0);
      handleClose();
    } else {
      setStep(step - 1);
    }
  }, [step, handleClose, initialState]);

  const handleSubmit = useCallback(
    value => {
      handleCreateContribution({ ...data, ...value });
    },
    [data, handleCreateContribution]
  );

  useEffect(() => {
    if (open) {
      setData(initialState);
      dispatch(loadEquipmentDetail(equipmentId));
    }
  }, [open, initialState, dispatch, equipmentId]);

  if (!open) {
    return null;
  }

  return (
    <Dialog
      id="form-new-contribution"
      classes={{ paper: classes.root }}
      open={open}
      scroll="body"
      TransitionComponent={Transition}
      keepMounted
      disableEscapeKeyDown
    >
      <DialogTitle className={classes.title} id="form-new-contribution-title">
        <IconButton
          key="close"
          aria-label="Close"
          color="inherit"
          onClick={() => {
            handleCloseDialog();
          }}
          className={classes.closeIcon}
        >
          <CloseIcon />
        </IconButton>
        <Typography className={classes.title} color="primary" variant="h2">
          {t('CONTRACT_VIEW.CONTRACT_DETAIL.CREATE_CONTRIBUTION')}
        </Typography>
      </DialogTitle>
      <Divider light variant="middle" />
      <DialogContent
        sx={{
          height: 'auto',
          maxHeight: 'calc(100vh - 200px)'
        }}
      >
        {balance > 0 && step === 0 && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            {t('COMMON.CAPITAL_CONTRIBUTION_WARNING', {
              balance: formatAmountWithDecimals(balance, 2)
            })}
          </Alert>
        )}

        <AlertWithLink
          severity="error"
          text={t('COMMON.SALE_PRODUCT_KEY_NEEDED_ERROR')}
          open={data.isPayoffRequest && !equipment?.saleProductKey}
          link={
            <Link to={`/inventory/${equipmentId}`}>
              {t('COMMON.GO_TO_EQUIPMENTS')}
            </Link>
          }
          hideCloseIcon
          sx={{ marginBottom: 3 }}
        />

        <FormStepper index={step} labels={stepperLabels} />
        <CurrentStep
          activeStep={step}
          data={data}
          resetRef={resetRef}
          onNext={handleNext}
          onBack={handleBackOrClose}
          onSubmit={handleSubmit}
          hasSaleProductKey={Boolean(equipment?.saleProductKey)}
        />
      </DialogContent>
    </Dialog>
  );
};

ContributionsForm.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleCreateContribution: PropTypes.func.isRequired,
  currentRent: PropTypes.number.isRequired,
  contractId: PropTypes.number.isRequired,
  equipmentId: PropTypes.number.isRequired
};

export default ContributionsForm;
